import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Phone, Clock, AlertCircle, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';

// CNR outcomes (Call Not Received) - these increment CNR counter
const CNR_OUTCOMES = ['No Answer', 'Busy', 'Voicemail', 'Wrong Number'];

const CALL_OUTCOMES = [
  { value: 'Qualified', label: '✅ Qualified', color: 'text-green-500', group: 'positive' },
  { value: 'Virtual Meet Scheduled', label: '✅ Virtual Meet Scheduled', color: 'text-blue-500', group: 'positive' },
  { value: 'Converted', label: '✅ Converted', color: 'text-emerald-500', group: 'positive' },
  { value: 'Call Back Later', label: '🔄 Call Back Later', color: 'text-yellow-500', group: 'neutral' },
  { value: 'Not Interested', label: '❌ Not Interested', color: 'text-red-500', group: 'negative' },
  { value: 'No Answer', label: '📵 No Answer (CNR)', color: 'text-orange-500', group: 'cnr' },
  { value: 'Busy', label: '📵 Busy (CNR)', color: 'text-orange-500', group: 'cnr' },
  { value: 'Voicemail', label: '📵 Voicemail (CNR)', color: 'text-purple-500', group: 'cnr' },
  { value: 'Wrong Number', label: '📵 Wrong Number (CNR)', color: 'text-red-400', group: 'cnr' },
];

const CallRemarkDialog = ({ open, onOpenChange, lead, onSubmit, api }) => {
  const [formData, setFormData] = useState({
    summary: '',
    leadResponse: '',
    nextAction: '',
    outcome: '',
    duration: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.summary.trim()) newErrors.summary = 'Call summary is required';
    if (!formData.leadResponse.trim()) newErrors.leadResponse = 'Lead response is required';
    if (!formData.nextAction.trim()) newErrors.nextAction = 'Next action is required';
    if (!formData.outcome) newErrors.outcome = 'Please select an outcome';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/calls/log', {
        leadId: lead.id,
        summary: formData.summary,
        leadResponse: formData.leadResponse,
        nextAction: formData.nextAction,
        outcome: formData.outcome,
        duration: formData.duration ? parseInt(formData.duration) : null
      });

      // Determine if this is a CNR outcome
      const isCNR = CNR_OUTCOMES.includes(formData.outcome);
      toast.success(isCNR 
        ? `Call logged! CNR count updated to ${(lead.cnrCount || 0) + 1}` 
        : 'Call logged successfully!'
      );
      
      // Reset form
      setFormData({
        summary: '',
        leadResponse: '',
        nextAction: '',
        outcome: '',
        duration: ''
      });
      setErrors({});
      
      if (onSubmit) {
        onSubmit(response.data);
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to log call:', error);
      toast.error('Failed to log call. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Warn user about unsaved data
    if (formData.summary || formData.leadResponse || formData.nextAction) {
      if (!window.confirm('You have unsaved call data. Are you sure you want to close?')) {
        return;
      }
    }
    setFormData({
      summary: '',
      leadResponse: '',
      nextAction: '',
      outcome: '',
      duration: ''
    });
    setErrors({});
    onOpenChange(false);
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="call-remark-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-green-500" />
            Log Call Remark
          </DialogTitle>
          <DialogDescription>
            Record the outcome of your call with <strong>{lead.name}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Lead Info Banner */}
          <div className="bg-muted/50 p-3 rounded-lg flex items-center justify-between">
            <div>
              <p className="font-medium">{lead.name}</p>
              <p className="text-sm text-muted-foreground">{lead.mobile}</p>
            </div>
            <div className="text-right flex gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="font-bold text-lg text-blue-600">{lead.totalCalls || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CNR</p>
                <p className="font-bold text-lg text-orange-600">{lead.cnrCount || 0}</p>
              </div>
            </div>
          </div>

          {/* CNR Warning */}
          {formData.outcome && CNR_OUTCOMES.includes(formData.outcome) && (
            <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700">
              <PhoneOff className="h-4 w-4" />
              <span>This outcome will increase CNR count (Call Not Received)</span>
            </div>
          )}

          {/* Call Summary */}
          <div className="space-y-2">
            <Label htmlFor="summary" className="flex items-center gap-1">
              Call Summary <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="summary"
              placeholder="Brief summary of the call..."
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              className={errors.summary ? 'border-red-500' : ''}
              rows={2}
              data-testid="call-summary-input"
            />
            {errors.summary && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.summary}
              </p>
            )}
          </div>

          {/* Lead Response */}
          <div className="space-y-2">
            <Label htmlFor="leadResponse" className="flex items-center gap-1">
              Lead Response <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="leadResponse"
              placeholder="How did the lead respond? What were their concerns?"
              value={formData.leadResponse}
              onChange={(e) => setFormData({ ...formData, leadResponse: e.target.value })}
              className={errors.leadResponse ? 'border-red-500' : ''}
              rows={2}
              data-testid="lead-response-input"
            />
            {errors.leadResponse && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.leadResponse}
              </p>
            )}
          </div>

          {/* Next Action */}
          <div className="space-y-2">
            <Label htmlFor="nextAction" className="flex items-center gap-1">
              Next Action <span className="text-red-500">*</span>
            </Label>
            <Input
              id="nextAction"
              placeholder="e.g., Schedule follow-up call, Send proposal..."
              value={formData.nextAction}
              onChange={(e) => setFormData({ ...formData, nextAction: e.target.value })}
              className={errors.nextAction ? 'border-red-500' : ''}
              data-testid="next-action-input"
            />
            {errors.nextAction && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.nextAction}
              </p>
            )}
          </div>

          {/* Outcome & Duration Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Outcome */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Call Outcome <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.outcome}
                onValueChange={(value) => setFormData({ ...formData, outcome: value })}
              >
                <SelectTrigger 
                  className={errors.outcome ? 'border-red-500' : ''}
                  data-testid="outcome-select"
                >
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  {CALL_OUTCOMES.map((outcome) => (
                    <SelectItem key={outcome.value} value={outcome.value}>
                      <span className={outcome.color}>{outcome.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.outcome && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.outcome}
                </p>
              )}
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label htmlFor="duration" className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> Duration (seconds)
              </Label>
              <Input
                id="duration"
                type="number"
                placeholder="e.g., 120"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                min="0"
                data-testid="duration-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            data-testid="submit-call-log-btn"
          >
            {isSubmitting ? 'Saving...' : 'Save Call Log'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CallRemarkDialog;
